import React, {FormEvent, useMemo, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Button} from '@ui/buttons/button';
import {Skeleton} from '@ui/skeleton/skeleton';
import {DashboardIcon} from '@ui/icons/material/Dashboard';
import {ApiIcon} from '@ui/icons/material/Api';
import {UploadFileIcon} from '@ui/icons/material/UploadFile';
import {AnalyticsIcon} from '@ui/icons/material/Analytics';
import {LibraryMusicIcon} from '@ui/icons/material/LibraryMusic';
import {AssessmentIcon} from '@ui/icons/material/Assessment';
import {PeopleIcon} from '@ui/icons/material/People';
import {WebhookIcon} from '@ui/icons/material/Webhook';
import {ContentCopyIcon} from '@ui/icons/material/ContentCopy';
import {DescriptionIcon} from '@ui/icons/material/Description';
import {AccountBalanceIcon} from '@ui/icons/material/AccountBalance';
import {ListAltIcon} from '@ui/icons/material/ListAlt';
import {AlbumIcon} from '@ui/icons/material/Album';
import {MusicNoteIcon} from '@ui/icons/material/MusicNote';
import {PersonIcon} from '@ui/icons/material/Person';
import {OpenInNewIcon} from '@ui/icons/material/OpenInNew';
import {DeleteForeverIcon} from '@ui/icons/material/DeleteForever';
import {
  useCreateGadTeamMember,
  useCreateGadWebhook,
  useDeleteGadTeamMember,
  useDeleteGadWebhook,
  useGadAnalytics,
  useGadApiKeys,
  useGadApiLogs,
  useGadCatalog,
  useGadFinance,
  useGadMe,
  useGadReports,
  useGadSummary,
  useGadTeam,
  useGadUploads,
  useGadWebhooks,
  useGenerateGadReport,
  useUpdateGadTeamMember,
  useUpdateGadWebhook,
} from './requests';
import {
  GadAnalytics,
  GadApiKey,
  GadApiUsageLog,
  GadCatalogOwnership,
  GadCatalogType,
  GadMenu,
  GadSubmission,
  GadWebhook,
} from './types';

const validationMessage =
  'Sua conta está em processo de validação e análise documental. Assim que a aprovação for concluída, o acesso completo ao painel e às APIs será liberado.';

const navItems: Array<{
  key: GadMenu;
  label: string;
  Icon: React.ComponentType<{className?: string}>;
}> = [
  {key: 'panel', label: 'Painel', Icon: DashboardIcon},
  {key: 'analytics', label: 'Analytics', Icon: AnalyticsIcon},
  {key: 'uploads', label: 'Uploads', Icon: UploadFileIcon},
  {key: 'catalog', label: 'Catálogo', Icon: LibraryMusicIcon},
  {key: 'reports', label: 'Relatórios', Icon: DescriptionIcon},
  {key: 'webhooks', label: 'Webhooks', Icon: WebhookIcon},
  {key: 'finance', label: 'Financeiro', Icon: AccountBalanceIcon},
  {key: 'team', label: 'Equipes', Icon: PeopleIcon},
  {key: 'apis', label: 'APIs', Icon: ApiIcon},
  {key: 'logs', label: 'Logs', Icon: ListAltIcon},
];

const catalogTabs: Array<{key: GadCatalogType; label: string; Icon: React.ComponentType<{className?: string}>}> = [
  {key: 'artists', label: 'Artists', Icon: PersonIcon},
  {key: 'albums', label: 'Albums', Icon: AlbumIcon},
  {key: 'tracks', label: 'Tracks', Icon: MusicNoteIcon},
];

const apiEndpoints = [
  ['POST', '/api/v1/gad-api/resolve', 'Resolver dedupe antes de criar'],
  ['POST', '/api/v1/gad-api/artists', 'Criar ou vincular artista'],
  ['POST', '/api/v1/gad-api/albums', 'Criar ou vincular álbum'],
  ['POST', '/api/v1/gad-api/tracks', 'Criar ou vincular track'],
  ['POST', '/api/v1/gad-api/uploads', 'Registrar upload assíncrono'],
  ['POST', '/api/v1/gad-api/assets', 'Registrar assets/capas'],
  ['GET', '/api/v1/gad-api/submissions/{external_id}', 'Consultar processamento'],
  ['GET', '/api/v1/gad-api/catalog', 'Consultar catálogo vinculado'],
  ['GET', '/api/v1/gad-api/analytics', 'Consultar analytics'],
];

export function DashboardPartnerGadPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useGadMe();
  const account = me.data;
  const menu = ((searchParams.get('menu') as GadMenu) || 'panel') as GadMenu;
  const approved = account?.status === 'approved';

  const setMenu = (nextMenu: GadMenu) => {
    const next = new URLSearchParams(searchParams);
    next.set('menu', nextMenu);
    setSearchParams(next);
  };

  if (me.isLoading) {
    return <GadShellLoading />;
  }

  if (!account) {
    return (
      <main className="min-h-screen bg-alt">
        <div className="mx-auto flex max-w-720 flex-col items-start px-24 py-64">
          <h1 className="mb-12 text-3xl font-semibold">Dashboard GAD</h1>
          <p className="mb-24 text-muted">
            Nenhuma conta parceira GAD foi encontrada para este usuário.
          </p>
          <Button elementType={Link} to="/register-partner-account-gad" variant="flat" color="primary">
            Criar conta parceira
          </Button>
        </div>
      </main>
    );
  }

  if (!approved) {
    const needsRevision = ['rejected', 'validation_required'].includes(account.status);
    const partnerMessage = needsRevision && account.revision_notes
      ? account.revision_notes
      : validationMessage;

    return (
      <main className="min-h-screen bg-alt">
        <div className="mx-auto max-w-920 px-24 py-64">
          <div className="rounded border bg-paper p-24">
            <div className="mb-8 text-sm font-semibold uppercase text-primary">
              {statusLabel(account.status)}
            </div>
            <h1 className="mb-16 text-3xl font-semibold">{account.company_name}</h1>
            <p className="text-base text-muted">{partnerMessage}</p>
            {needsRevision && (
              <Button
                className="mt-20"
                elementType={Link}
                to="/register-partner-account-gad"
                variant="flat"
                color="primary"
              >
                Corrigir cadastro
              </Button>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-full bg-background text-main">
      <Navbar
        color="bg"
        darkModeColor="bg"
        logoColor="dark"
        textColor="text-main"
        size="sm"
        border="border-b"
        className="sticky top-0 z-40"
      >
        <div className="mr-auto flex h-full min-w-0 items-center border-l pl-14 text-lg font-bold tracking-tight md:pl-18">
          <span className="truncate">Partner Studio · {account.trade_name || account.company_name}</span>
        </div>
      </Navbar>
      <div className="grid min-h-[calc(100vh-54px)] lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="border-b bg-background px-16 py-18 lg:border-b-0 lg:border-r lg:px-18">
          <PartnerSidebar
            accountName={account.trade_name || account.company_name}
            accountType={companyTypeLabel(account.company_type)}
            status={account.status}
            menu={menu}
            onMenuChange={setMenu}
          />
        </aside>
        <main className="min-w-0 px-16 py-20 md:px-24 lg:px-32">
          {menu === 'panel' && <PanelView />}
          {menu === 'analytics' && <AnalyticsView />}
          {menu === 'uploads' && <UploadsView />}
          {menu === 'catalog' && <CatalogView />}
          {menu === 'reports' && <ReportsView />}
          {menu === 'webhooks' && <WebhooksView />}
          {menu === 'finance' && <FinanceView />}
          {menu === 'team' && <TeamView />}
          {menu === 'apis' && <ApisView />}
          {menu === 'logs' && <LogsView />}
        </main>
      </div>
    </div>
  );
}

function PartnerSidebar({
  accountName,
  accountType,
  status,
  menu,
  onMenuChange,
}: {
  accountName: string;
  accountType: string;
  status: string;
  menu: GadMenu;
  onMenuChange: (menu: GadMenu) => void;
}) {
  return (
    <div className="lg:sticky lg:top-24">
      <div className="flex items-center gap-12 lg:block">
        <div className="flex items-center gap-12 lg:flex-col">
          <div className="flex h-56 w-56 shrink-0 items-center justify-center rounded-full bg-alt text-xl font-semibold lg:h-96 lg:w-96">
            {accountName[0] || 'G'}
          </div>
          <div className="min-w-0 lg:mt-10 lg:text-center">
            <div className="font-semibold">Conta GAD</div>
            <div className="truncate text-sm text-muted">{accountName}</div>
          </div>
        </div>
      </div>
      <div className="mt-16 rounded border bg-paper p-12">
        <div className="text-xs font-semibold uppercase text-muted">Status</div>
        <div className="mt-6 flex items-center justify-between gap-8">
          <StatusBadge status={status} />
          <span className="text-xs text-muted">{accountType}</span>
        </div>
      </div>
      <nav className="mt-18 grid gap-4">
        {navItems.map(item => (
          <button
            key={item.key}
            type="button"
            onClick={() => onMenuChange(item.key)}
            className={clsx(
              'flex h-48 items-center gap-14 rounded px-12 text-left text-sm font-medium transition-colors',
              menu === item.key
                ? 'bg-alt text-main'
                : 'text-muted hover:bg-alt hover:text-main',
            )}
          >
            <item.Icon className="icon-md" />
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

function PanelView() {
  const summary = useGadSummary(true);
  const data = summary.data;

  if (summary.isLoading || !data) {
    return <SectionSkeleton title="Painel do parceiro" />;
  }

  return (
    <section>
      <HeaderBlock
        title="Painel do parceiro"
        subtitle="Resumo operacional do catálogo, APIs, uploads, webhooks e performance do parceiro."
      />
      <div className="mt-22 grid gap-14 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard title="Streams" value={data.activity.streams} Icon={AnalyticsIcon} />
        <SummaryCard title="Tracks" value={data.catalog.tracks} Icon={MusicNoteIcon} />
        <SummaryCard title="Albums" value={data.catalog.albums} Icon={AlbumIcon} />
        <SummaryCard title="Artists" value={data.catalog.artists} Icon={PersonIcon} />
        <SummaryCard title="Submissions" value={data.activity.submissions} Icon={UploadFileIcon} />
        <SummaryCard title="API Calls" value={data.activity.api_calls} Icon={ApiIcon} />
        <SummaryCard title="Webhooks ativos" value={data.activity.active_webhooks} Icon={WebhookIcon} />
        <SummaryCard title="Equipe" value={data.activity.team_members} Icon={PeopleIcon} />
      </div>
      <div className="mt-24 grid gap-18 xl:grid-cols-3">
        <Panel title="Top Tracks">
          <RankedList items={data.analytics.tracks.top_tracks} />
        </Panel>
        <Panel title="Últimos uploads">
          <SubmissionList items={data.recent_submissions} />
        </Panel>
        <Panel title="Últimas chamadas API">
          <ApiLogList items={data.recent_api_usage} />
        </Panel>
      </div>
    </section>
  );
}

function AnalyticsView() {
  const analytics = useGadAnalytics(true);
  const data = analytics.data;

  if (analytics.isLoading || !data) {
    return <SectionSkeleton title="Analytics" />;
  }

  return (
    <section>
      <HeaderBlock
        title="Analytics"
        subtitle="Streams, retenção, saves, shares, crescimento diário, geografia e tecnologia."
      />
      <div className="mt-22 grid gap-14 md:grid-cols-2 xl:grid-cols-4">
        <Metric label="Total Streams" value={data.tracks.total_streams} />
        <Metric label="Likes" value={data.tracks.total_likes} />
        <Metric label="Shares" value={data.tracks.total_shares} />
        <Metric label="Playlist Adds" value={data.tracks.playlist_adds} />
        <Metric label="Saves" value={data.tracks.saves} />
        <Metric label="Retenção" value={data.tracks.retention_seconds} suffix="s" />
        <Metric label="Plays Albums" value={data.albums.total_plays} />
        <Metric label="Albums Saves" value={data.albums.saves} />
      </div>
      <div className="mt-24 grid gap-18 xl:grid-cols-2">
        <Panel title="Crescimento diário">
          <Breakdown
            items={data.tracks.daily_growth.map(item => ({label: item.date, value: item.value}))}
            empty="Sem crescimento diário registrado ainda."
          />
        </Panel>
        <Panel title="Top Tracks">
          <RankedList items={data.tracks.top_tracks} />
        </Panel>
        <Panel title="Top Albums">
          <RankedList items={data.albums.top_albums} />
        </Panel>
        <Panel title="Geografia">
          <Breakdown items={data.geography} empty="Sem dados geográficos ainda." />
        </Panel>
        <Panel title="Técnico">
          <div className="grid gap-16 md:grid-cols-3">
            <Breakdown title="Dispositivo" items={data.technical.device} />
            <Breakdown title="Sistema" items={data.technical.platform} />
            <Breakdown title="Plataforma" items={data.technical.browser} />
          </div>
        </Panel>
      </div>
    </section>
  );
}

function UploadsView() {
  const [status, setStatus] = useState('');
  const params = useMemo(() => (status ? {status} : {}), [status]);
  const uploads = useGadUploads(true, params);
  const submissions = uploads.data?.submissions.data || [];

  return (
    <section>
      <HeaderBlock
        title="Uploads"
        subtitle="Submissions recebidas via API, documentos vinculados e status de processamento."
      />
      <div className="mt-18 flex flex-wrap items-center gap-10">
        <select value={status} onChange={event => setStatus(event.target.value)} className="h-42 rounded border bg-background px-12">
          <option value="">Todos os status</option>
          {['received', 'validated', 'linked', 'created', 'needs_review', 'failed'].map(item => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </div>
      <div className="mt-18 grid gap-18 xl:grid-cols-[minmax(0,1fr)_340px]">
        <Panel title="Submissions">
          {uploads.isLoading ? <TableSkeleton /> : <SubmissionTable items={submissions} />}
        </Panel>
        <Panel title="Documentos">
          <div className="space-y-10">
            {(uploads.data?.documents || []).map(document => (
              <div key={document.id} className="border-b pb-10 last:border-b-0">
                <div className="font-semibold">{document.name || document.type}</div>
                <div className="text-xs text-muted">{document.status} · {formatDate(document.created_at)}</div>
              </div>
            ))}
            {!uploads.data?.documents?.length && <EmptyState text="Nenhum documento listado." />}
          </div>
        </Panel>
      </div>
    </section>
  );
}

function CatalogView() {
  const [type, setType] = useState<GadCatalogType>('tracks');
  const catalog = useGadCatalog(true, type);
  const items = catalog.data?.ownerships.data || [];

  return (
    <section>
      <HeaderBlock
        title="Catálogo"
        subtitle="Conteúdos vinculados por ownership/distribuição, sem duplicação de artistas, albums ou tracks."
      />
      <div className="mt-18 flex flex-wrap gap-8">
        {catalogTabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setType(tab.key)}
            className={clsx(
              'inline-flex h-40 items-center gap-8 rounded border px-12 text-sm font-semibold',
              type === tab.key ? 'border-primary bg-primary/10 text-primary' : 'text-muted hover:text-main',
            )}
          >
            <tab.Icon className="icon-sm" />
            {tab.label}
          </button>
        ))}
      </div>
      <Panel className="mt-18" title="Ownerships">
        {catalog.isLoading ? <TableSkeleton /> : <CatalogTable items={items} />}
      </Panel>
    </section>
  );
}

function ReportsView() {
  const reports = useGadReports(true);
  const generate = useGenerateGadReport();
  const [generated, setGenerated] = useState<Record<string, unknown> | null>(null);

  const runReport = async (type: string) => {
    const report = await generate.mutateAsync({type, format: 'json', period_days: 30});
    setGenerated(report);
  };

  return (
    <section>
      <HeaderBlock
        title="Relatórios"
        subtitle="Relatórios sob demanda para analytics, catálogo, uso da API, submissions e royalties."
      />
      <div className="mt-22 grid gap-14 md:grid-cols-2 xl:grid-cols-3">
        {(reports.data?.reports || []).map(report => (
          <div key={report.type} className="rounded border bg-paper p-16">
            <div className="text-lg font-semibold">{report.name}</div>
            <p className="mt-8 min-h-40 text-sm text-muted">{report.description}</p>
            <Button className="mt-14" variant="outline" size="sm" disabled={generate.isPending} onClick={() => runReport(report.type)}>
              Gerar JSON
            </Button>
          </div>
        ))}
      </div>
      {generated && (
        <Panel className="mt-18" title="Último relatório gerado">
          <CodeBlock value={JSON.stringify(generated, null, 2)} />
        </Panel>
      )}
    </section>
  );
}

function WebhooksView() {
  const webhooks = useGadWebhooks(true);
  const createWebhook = useCreateGadWebhook();
  const updateWebhook = useUpdateGadWebhook();
  const deleteWebhook = useDeleteGadWebhook();
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const response = await createWebhook.mutateAsync({
      url,
      status: 'active',
      events: webhooks.data?.events || ['submission.received', 'content.linked', 'content.created'],
    });
    setUrl('');
    setSecret(response.signing_secret);
  };

  return (
    <section>
      <HeaderBlock
        title="Webhooks"
        subtitle="Notificações assinadas para submissions, links, criações, falhas e analytics."
      />
      <div className="mt-22 grid gap-18 xl:grid-cols-[minmax(0,1fr)_420px]">
        <Panel title="Endpoints">
          <form onSubmit={submit} className="mb-16 flex flex-wrap gap-10">
            <input
              value={url}
              onChange={event => setUrl(event.target.value)}
              placeholder="https://empresa.com/webhooks/sintonizy"
              className="h-42 min-w-260 flex-auto rounded border bg-background px-12"
            />
            <Button type="submit" variant="flat" color="primary" disabled={!url || createWebhook.isPending}>
              Adicionar
            </Button>
          </form>
          {secret && (
            <div className="mb-14 rounded border border-positive bg-positive/10 p-12 text-sm">
              <div className="mb-6 font-semibold">Segredo exibido uma única vez</div>
              <div className="flex items-center gap-10">
                <code className="min-w-0 flex-auto break-all">{secret}</code>
                <CopyButton value={secret} />
              </div>
            </div>
          )}
          <div className="space-y-10">
            {(webhooks.data?.webhooks || []).map(webhook => (
              <WebhookRow
                key={webhook.id}
                webhook={webhook}
                onToggle={() => updateWebhook.mutate({id: webhook.id, payload: {status: webhook.status === 'active' ? 'inactive' : 'active'}})}
                onRotate={async () => {
                  const response = await updateWebhook.mutateAsync({id: webhook.id, payload: {rotate_secret: true}});
                  if (response.signing_secret) setSecret(response.signing_secret);
                }}
                onDelete={() => deleteWebhook.mutate(webhook.id)}
              />
            ))}
            {!webhooks.data?.webhooks?.length && <EmptyState text="Nenhum webhook configurado." />}
          </div>
        </Panel>
        <Panel title="Documentação">
          <div className="mb-12 text-sm text-muted">Headers de assinatura</div>
          <CodeBlock value={`X-Sintonizy-Signature: sha256=<hmac>\nX-Sintonizy-Timestamp: <unix>\nX-Sintonizy-Delivery: <uuid>`} />
          <div className="mt-16 text-sm font-semibold">Eventos</div>
          <div className="mt-8 flex flex-wrap gap-8">
            {(webhooks.data?.events || []).map(event => (
              <span key={event} className="rounded bg-alt px-8 py-4 text-xs">{event}</span>
            ))}
          </div>
        </Panel>
      </div>
    </section>
  );
}

function FinanceView() {
  const finance = useGadFinance(true);
  const items = finance.data?.pagination.data || [];

  return (
    <section>
      <HeaderBlock
        title="Financeiro"
        subtitle="V1 read-only com ownerships, territórios, percentuais e splits enviados nos metadados."
      />
      <div className="mt-22 grid gap-14 md:grid-cols-3">
        <SummaryCard title="Ownerships" value={finance.data?.summary.ownerships || 0} Icon={LibraryMusicIcon} />
        <SummaryCard title="Com percentual" value={finance.data?.summary.with_percent || 0} Icon={AssessmentIcon} />
        <SummaryCard title="Territórios" value={finance.data?.summary.territories || 0} Icon={AccountBalanceIcon} />
      </div>
      <Panel className="mt-18" title="Royalties e splits">
        {finance.isLoading ? <TableSkeleton /> : <FinanceTable items={items} />}
      </Panel>
    </section>
  );
}

function TeamView() {
  const team = useGadTeam(true);
  const createMember = useCreateGadTeamMember();
  const updateMember = useUpdateGadTeamMember();
  const deleteMember = useDeleteGadTeamMember();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('developer');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await createMember.mutateAsync({email, name, role});
    setEmail('');
    setName('');
    setRole('developer');
  };

  return (
    <section>
      <HeaderBlock
        title="Equipes"
        subtitle="Gerencie pessoas e papéis operacionais para catálogo, analytics, financeiro e integrações."
      />
      <Panel className="mt-22" title="Adicionar membro">
        <form onSubmit={submit} className="grid gap-10 md:grid-cols-[1fr_1fr_220px_auto]">
          <input value={name} onChange={event => setName(event.target.value)} placeholder="Nome" className="h-42 rounded border bg-background px-12" />
          <input value={email} onChange={event => setEmail(event.target.value)} placeholder="email@empresa.com" className="h-42 rounded border bg-background px-12" />
          <select value={role} onChange={event => setRole(event.target.value)} className="h-42 rounded border bg-background px-12">
            {Object.entries(team.data?.roles || {}).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <Button type="submit" variant="flat" color="primary" disabled={!email || createMember.isPending}>Adicionar</Button>
        </form>
      </Panel>
      <Panel className="mt-18" title="Membros">
        <div className="space-y-10">
          {(team.data?.team_members || []).map(member => (
            <div key={member.id} className="flex flex-wrap items-center justify-between gap-12 border-b pb-10 last:border-b-0">
              <div>
                <div className="font-semibold">{member.name || member.email}</div>
                <div className="text-xs text-muted">{member.email} · {member.role} · {member.status}</div>
              </div>
              <div className="flex gap-8">
                <Button variant="outline" size="xs" onClick={() => updateMember.mutate({id: member.id, payload: {status: member.status === 'active' ? 'disabled' : 'active'}})}>
                  {member.status === 'active' ? 'Desativar' : 'Ativar'}
                </Button>
                <Button variant="outline" color="danger" size="xs" startIcon={<DeleteForeverIcon />} onClick={() => deleteMember.mutate(member.id)}>
                  Remover
                </Button>
              </div>
            </div>
          ))}
          {!team.data?.team_members?.length && <EmptyState text="Nenhum membro cadastrado." />}
        </div>
      </Panel>
    </section>
  );
}

function ApisView() {
  const keys = useGadApiKeys(true);
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const activeKey = keys.data?.find(key => key.environment === environment && key.plain_text_key) || keys.data?.find(key => key.environment === environment);
  const keyValue = activeKey?.plain_text_key || activeKey?.key_prefix || `<${environment === 'production' ? 'PRODUCTION' : 'SANDBOX'}_API_KEY>`;

  return (
    <section>
      <HeaderBlock
        title="APIs"
        subtitle="Chaves, scopes e documentação para integração enterprise com dedupe inteligente."
      />
      <div className="mt-22 grid gap-18 xl:grid-cols-[minmax(0,1fr)_420px]">
        <Panel title="API Keys">
          <div className="space-y-10">
            {(keys.data || []).map(key => <ApiKeyRow key={key.id} apiKey={key} />)}
            {!keys.data?.length && <EmptyState text="Nenhuma API key gerada ainda." />}
          </div>
        </Panel>
        <Panel title="Exemplo cURL">
          <select value={environment} onChange={event => setEnvironment(event.target.value as 'sandbox' | 'production')} className="mb-12 h-40 rounded border bg-background px-10">
            <option value="sandbox">Sandbox</option>
            <option value="production">Produção</option>
          </select>
          <CodeBlock value={`curl -X POST https://vibe.sintonizy.com/api/v1/gad-api/tracks \\\n  -H "Authorization: Bearer ${keyValue}" \\\n  -H "Content-Type: application/json" \\\n  -H "Idempotency-Key: partner-track-001" \\\n  -d '${JSON.stringify({external_id: 'partner-track-001', name: 'Minha faixa', isrc: 'BRABC2400001', artists: [{name: 'Artista Existente'}]}, null, 2)}'`} />
        </Panel>
      </div>
      <Panel className="mt-18" title="Endpoints externos">
        <div className="grid gap-8">
          {apiEndpoints.map(([method, endpoint, description]) => (
            <div key={endpoint} className="grid gap-10 rounded border p-12 text-sm md:grid-cols-[80px_minmax(0,1fr)_minmax(0,1.2fr)]">
              <span className="font-semibold text-primary">{method}</span>
              <code className="break-all">{endpoint}</code>
              <span className="text-muted">{description}</span>
            </div>
          ))}
        </div>
      </Panel>
    </section>
  );
}

function LogsView() {
  const [environment, setEnvironment] = useState('');
  const [statusCode, setStatusCode] = useState('');
  const params = useMemo(() => {
    const next: Record<string, string> = {};
    if (environment) next.environment = environment;
    if (statusCode) next.status_code = statusCode;
    return next;
  }, [environment, statusCode]);
  const logs = useGadApiLogs(true, params);

  return (
    <section>
      <HeaderBlock
        title="Logs"
        subtitle="Chamadas da API com endpoint, método, status, latência e request id, sem payload bruto sensível."
      />
      <div className="mt-18 flex flex-wrap gap-10">
        <select value={environment} onChange={event => setEnvironment(event.target.value)} className="h-42 rounded border bg-background px-12">
          <option value="">Todos ambientes</option>
          <option value="sandbox">Sandbox</option>
          <option value="production">Produção</option>
        </select>
        <input value={statusCode} onChange={event => setStatusCode(event.target.value)} placeholder="Status code" className="h-42 rounded border bg-background px-12" />
      </div>
      <Panel className="mt-18" title="Uso da API">
        {logs.isLoading ? <TableSkeleton /> : <LogsTable items={logs.data?.data || []} />}
      </Panel>
    </section>
  );
}

function SummaryCard({title, value, Icon}: {title: string; value: number; Icon: React.ComponentType<{className?: string}>}) {
  return (
    <div className="rounded border bg-paper p-16">
      <div className="mb-12 flex items-center justify-between gap-12">
        <div className="text-sm font-semibold text-muted">{title}</div>
        <Icon className="icon-md text-primary" />
      </div>
      <div className="text-3xl font-semibold">{formatNumber(value)}</div>
    </div>
  );
}

function Metric({label, value, suffix}: {label: string; value?: number; suffix?: string}) {
  return (
    <div className="rounded border bg-paper p-16">
      <div className="mb-8 text-xs font-semibold uppercase text-muted">{label}</div>
      <div className="text-3xl font-semibold">{formatNumber(value || 0)}{suffix || ''}</div>
    </div>
  );
}

function HeaderBlock({title, subtitle}: {title: string; subtitle: string}) {
  return (
    <div>
      <h1 className="text-3xl font-semibold">{title}</h1>
      <p className="mt-6 max-w-860 text-sm text-muted">{subtitle}</p>
    </div>
  );
}

function Panel({title, children, className}: {title: string; children: React.ReactNode; className?: string}) {
  return (
    <section className={clsx('rounded border bg-paper p-16', className)}>
      <h2 className="mb-14 text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function RankedList({items}: {items: GadAnalytics['tracks']['top_tracks']}) {
  if (!items.length) return <EmptyState text="Sem dados suficientes ainda." />;
  return (
    <div className="space-y-10">
      {items.slice(0, 8).map((item, index) => (
        <div key={`${item.id}-${index}`} className="flex items-center justify-between gap-12">
          <div className="min-w-0">
            <div className="truncate font-semibold">{index + 1}. {item.name}</div>
            {item.artist && <div className="truncate text-xs text-muted">{item.artist}</div>}
          </div>
          <div className="text-sm font-semibold">{formatNumber(item.plays)}</div>
        </div>
      ))}
    </div>
  );
}

function SubmissionList({items}: {items: GadSubmission[]}) {
  if (!items.length) return <EmptyState text="Nenhuma submission recente." />;
  return (
    <div className="space-y-10">
      {items.map(item => (
        <div key={item.id} className="border-b pb-10 last:border-b-0">
          <div className="flex justify-between gap-12">
            <span className="font-semibold">{item.content_type}</span>
            <StatusBadge status={item.status} />
          </div>
          <div className="mt-4 text-xs text-muted">{item.external_id || item.id} · {item.environment}</div>
        </div>
      ))}
    </div>
  );
}

function ApiLogList({items}: {items: GadApiUsageLog[]}) {
  if (!items.length) return <EmptyState text="Nenhum log recente." />;
  return (
    <div className="space-y-10">
      {items.map(item => (
        <div key={item.id} className="border-b pb-10 last:border-b-0">
          <div className="truncate font-semibold">{item.method} {item.endpoint}</div>
          <div className="mt-4 text-xs text-muted">{item.status_code || 'N/A'} · {item.latency_ms || 0}ms · {formatDate(item.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

function SubmissionTable({items}: {items: GadSubmission[]}) {
  if (!items.length) return <EmptyState text="Nenhuma submission encontrada." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-muted">
          <tr><th className="py-8">ID</th><th>Tipo</th><th>Status</th><th>Resultado</th><th>Criado</th></tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} className="border-t">
              <td className="py-10">{item.external_id || item.id}</td>
              <td>{item.content_type}</td>
              <td><StatusBadge status={item.status} /></td>
              <td className="max-w-300 truncate">{item.matched_model_type ? `${item.matched_model_type} #${item.matched_model_id}` : item.error_message || item.action}</td>
              <td>{formatDate(item.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CatalogTable({items}: {items: GadCatalogOwnership[]}) {
  if (!items.length) return <EmptyState text="Nenhum item vinculado neste tipo." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-muted">
          <tr><th className="py-8">Conteúdo</th><th>ID interno</th><th>Role</th><th>Território</th><th>IDs externos</th><th>Ações</th></tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} className="border-t">
              <td className="min-w-220 py-10">
                <div className="font-semibold">{item.content?.name || `${item.model_type} #${item.model_id}`}</div>
                <div className="text-xs text-muted">{item.model_type}</div>
              </td>
              <td><code>{item.model_id}</code></td>
              <td>{item.role}</td>
              <td>{item.territory || 'WORLD'}</td>
              <td className="max-w-260 truncate">
                {(item.external_identifiers || []).map(id => `${id.source}:${id.value}`).join(' · ') || 'N/A'}
              </td>
              <td>
                <div className="flex flex-wrap gap-8">
                  <CopyButton value={String(item.model_id)} label="ID" />
                  <CopyButton value={JSON.stringify(examplePayload(item), null, 2)} label="Payload" />
                  <Button variant="outline" size="xs" startIcon={<OpenInNewIcon />} elementType={Link} to={catalogLink(item)}>
                    Abrir
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinanceTable({items}: {items: GadCatalogOwnership[]}) {
  if (!items.length) return <EmptyState text="Nenhum dado financeiro disponível ainda." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-muted">
          <tr><th className="py-8">Conteúdo</th><th>Percentual</th><th>Território</th><th>Splits payload</th><th>Vigência</th></tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} className="border-t">
              <td className="py-10">{item.content?.name || `${item.model_type} #${item.model_id}`}</td>
              <td>{item.ownership_percent || 'N/A'}</td>
              <td>{item.territory || 'WORLD'}</td>
              <td className="max-w-360 truncate">{JSON.stringify(item.payload_splits || [])}</td>
              <td>{item.starts_at || 'N/A'} - {item.ends_at || 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LogsTable({items}: {items: GadApiUsageLog[]}) {
  if (!items.length) return <EmptyState text="Nenhum log encontrado." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-muted">
          <tr><th className="py-8">Data</th><th>Ambiente</th><th>Método</th><th>Endpoint</th><th>Status</th><th>Latência</th><th>Request ID</th></tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} className="border-t">
              <td className="py-10">{formatDate(item.created_at)}</td>
              <td>{item.environment || 'N/A'}</td>
              <td>{item.method}</td>
              <td className="max-w-360 truncate">{item.endpoint}</td>
              <td>{item.status_code || 'N/A'}</td>
              <td>{item.latency_ms || 0}ms</td>
              <td className="max-w-240 truncate">{item.request_id || item.error_code || 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApiKeyRow({apiKey}: {apiKey: GadApiKey}) {
  const value = apiKey.plain_text_key || apiKey.key_prefix;
  return (
    <div className="flex flex-wrap items-center justify-between gap-12 border-b pb-10 last:border-b-0">
      <div className="min-w-0">
        <div className="font-semibold">{apiKey.name || `${apiKey.environment} API Key`}</div>
        <div className="text-xs text-muted">
          {apiKey.environment} · {apiKey.status} · {apiKey.scopes?.join(', ') || 'sem scopes'} · último uso {formatDate(apiKey.last_used_at)}
        </div>
        <code className="mt-4 block break-all text-xs">{value}</code>
        {!apiKey.plain_text_key && <div className="mt-4 text-xs text-muted">Chave completa indisponível para keys antigas.</div>}
      </div>
      <CopyButton value={value} label={apiKey.plain_text_key ? 'Copiar' : 'Prefixo'} />
    </div>
  );
}

function WebhookRow({webhook, onToggle, onRotate, onDelete}: {webhook: GadWebhook; onToggle: () => void; onRotate: () => void; onDelete: () => void}) {
  return (
    <div className="rounded border p-12">
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="min-w-0">
          <div className="truncate font-semibold">{webhook.url}</div>
          <div className="mt-4 text-xs text-muted">
            {webhook.status} · sucesso {formatDate(webhook.last_success_at)} · falha {formatDate(webhook.last_failure_at)}
          </div>
          {webhook.last_error && <div className="mt-6 text-xs text-danger">{webhook.last_error}</div>}
        </div>
        <div className="flex gap-8">
          <Button variant="outline" size="xs" onClick={onToggle}>{webhook.status === 'active' ? 'Desativar' : 'Ativar'}</Button>
          <Button variant="outline" size="xs" onClick={onRotate}>Rotacionar segredo</Button>
          <Button variant="outline" color="danger" size="xs" startIcon={<DeleteForeverIcon />} onClick={onDelete}>Remover</Button>
        </div>
      </div>
      <div className="mt-10 flex flex-wrap gap-6">
        {(webhook.events || []).map(event => <span key={event} className="rounded bg-alt px-8 py-4 text-xs">{event}</span>)}
      </div>
    </div>
  );
}

function Breakdown({title, items, empty = 'Sem dados ainda.'}: {title?: string; items: Array<{label: string; value: number}>; empty?: string}) {
  return (
    <div>
      {title && <div className="mb-8 text-sm font-semibold">{title}</div>}
      {items.length ? (
        <div className="space-y-8">
          {items.slice(0, 10).map(item => (
            <div key={item.label || 'unknown'} className="flex items-center justify-between gap-12 text-sm">
              <span className="truncate text-muted">{item.label || 'N/A'}</span>
              <span className="font-semibold">{formatNumber(item.value)}</span>
            </div>
          ))}
        </div>
      ) : <EmptyState text={empty} />}
    </div>
  );
}

function CopyButton({value, label = 'Copiar'}: {value: string; label?: string}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <Button variant="outline" size="xs" startIcon={<ContentCopyIcon />} onClick={copy}>
      {copied ? 'Copiado' : label}
    </Button>
  );
}

function StatusBadge({status}: {status: string}) {
  const tone = status.includes('failed') || status.includes('rejected') || status.includes('blocked')
    ? 'bg-danger/10 text-danger'
    : status.includes('active') || status.includes('approved') || status.includes('created') || status.includes('linked')
      ? 'bg-positive/10 text-positive'
      : status.includes('review') || status.includes('pending')
        ? 'bg-warning/10 text-warning'
        : 'bg-alt text-muted';

  return <span className={clsx('inline-flex h-24 items-center rounded px-8 text-xs font-semibold', tone)}>{statusLabel(status)}</span>;
}

function CodeBlock({value}: {value: string}) {
  return <pre className="max-h-420 overflow-auto whitespace-pre-wrap rounded border bg-alt p-12 text-xs"><code>{value}</code></pre>;
}

function EmptyState({text}: {text: string}) {
  return <div className="rounded border border-dashed p-14 text-sm text-muted">{text}</div>;
}

function SectionSkeleton({title}: {title: string}) {
  return (
    <section>
      <HeaderBlock title={title} subtitle="Carregando dados..." />
      <div className="mt-22 grid gap-14 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({length: 8}).map((_, index) => <Skeleton key={index} className="h-112" />)}
      </div>
    </section>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-10">
      {Array.from({length: 6}).map((_, index) => <Skeleton key={index} className="h-48" />)}
    </div>
  );
}

function GadShellLoading() {
  return (
    <main className="min-h-screen bg-background p-24">
      <Skeleton className="mb-24 h-44 w-280" />
      <div className="grid gap-18 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Skeleton className="h-640" />
        <div className="space-y-16">
          <Skeleton className="h-120" />
          <Skeleton className="h-360" />
        </div>
      </div>
    </main>
  );
}

function examplePayload(item: GadCatalogOwnership) {
  const type = item.model_type;
  return {
    [`${type}_id`]: item.model_id,
    name: item.content?.name,
    ownership: {
      role: item.role,
      territory: item.territory || 'WORLD',
      percent: item.ownership_percent,
    },
  };
}

function catalogLink(item: GadCatalogOwnership) {
  const slug = encodeURIComponent((item.content?.name || item.model_type).toLowerCase().replace(/\s+/g, '-'));
  return `/${item.model_type}/${item.model_id}/${slug}`;
}

async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value || 0);
}

function formatDate(date?: string | null) {
  return date ? new Date(date).toLocaleDateString() : 'N/A';
}

function statusLabel(status: string): string {
  return {
    pending_review: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    suspended: 'Suspended',
    validation_required: 'Validation Required',
    blocked: 'Blocked',
  }[status] || status;
}

function companyTypeLabel(type: string): string {
  return {
    label: 'Gravadora',
    aggregator: 'Agregadora',
    distributor: 'Distribuidora',
  }[type] || type;
}
